import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003';

/**
 * Converte os filtros do dashboard em query params planos.
 * Arrays viram CSV (accountIds=1,2) para manter as URLs legíveis — inclusive as
 * de exportação, que são abertas direto pelo navegador.
 */
export const buildFilterParams = (filters = {}) => {
  const params = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    if (Array.isArray(value)) {
      if (value.length > 0) params[key] = value.join(',');
      return;
    }

    params[key] = value;
  });

  return params;
};

/** Mesma serialização, pronta para concatenar numa URL (window.open de export). */
export const buildFilterQueryString = (filters = {}) =>
  new URLSearchParams(buildFilterParams(filters)).toString();

const api = axios.create({
  baseURL: API_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: { serialize: (params) => new URLSearchParams(params).toString() },
});

// Interceptor para logging (desenvolvimento)
api.interceptors.request.use(
  (config) => {
    console.log(`🚀 Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    console.log(`✅ Response: ${response.config.url}`, response.data);
    return response;
  },
  (error) => {
    console.error('❌ Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

const withFilters = (path) => (filters) => api.get(path, { params: buildFilterParams(filters) });

// Serviços de métricas
export const metricsService = {
  getDashboard: withFilters('/api/metrics/dashboard'),
  getServiceLevel: withFilters('/api/metrics/service-level'),
  getTMA: withFilters('/api/metrics/tma'),
  getTME: withFilters('/api/metrics/tme'),
  getCSAT: withFilters('/api/metrics/csat'),
  getChannelDistribution: withFilters('/api/metrics/channel-distribution'),
  getHeatMap: withFilters('/api/metrics/heatmap'),
  getChannelRanking: withFilters('/api/metrics/channel-ranking'),
  getSLAAlerts: withFilters('/api/metrics/sla-alerts'),
  getAgentMetrics: withFilters('/api/metrics/agent-metrics'),
  getAiVsHuman: withFilters('/api/metrics/ai-vs-human'),
};

// Serviços de contas (filtro raiz do dashboard)
export const accountsService = {
  getAll: () => api.get('/api/accounts'),
};

// Serviços de times
export const teamsService = {
  getAll: (filters) => api.get('/api/teams', { params: buildFilterParams(filters) }),
};

// Serviços de agentes
export const agentsService = {
  getAll: (filters) => api.get('/api/agents', { params: buildFilterParams(filters) }),
  getById: (id) => api.get(`/api/agents/${id}`),
};

// Serviços de canais
export const channelsService = {
  getAll: (filters) => api.get('/api/channels', { params: buildFilterParams(filters) }),
  getById: (id) => api.get(`/api/channels/${id}`),
  getTypes: (filters) => api.get('/api/channels/types/list', { params: buildFilterParams(filters) }),
};

/** URLs de exportação (abertas via window.open, fora do axios). */
export const exportUrls = {
  conversations: (filters) => `${API_URL}/api/metrics/export/excel?${buildFilterQueryString(filters)}`,
  agentConversations: (agentId, filters) =>
    `${API_URL}/api/metrics/export/agent-conversations/${agentId}?${buildFilterQueryString(filters)}`,
  agentMetrics: (filters) => `${API_URL}/api/metrics/export/agent-metrics?${buildFilterQueryString(filters)}`,
};

export default api;
